import { useEffect, useState } from "react";
import axiosInstance from "config/axiosInstance";
import { Card, LinearProgress } from "@mui/material";
import { getCurrentUser } from "utils/auth";

import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import PieChart from "examples/Charts/PieCharts/PieChart";
import YapeLogo from "assets/images/logos/yape.svg";
import PlinLogo from "assets/images/logos/plin.svg";

function BancosUsados() {
  const [loading, setLoading] = useState(true);
  const [bancos, setBancos] = useState([]);
  const [totalDepositos, setTotalDepositos] = useState(0);

  const formatCurrency = (amount) => {
    const num = Number(amount || 0);
    return num.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatBancoName = (dominio) => {
    switch (dominio) {
      case "com.bcp.innovacxion.yapeapp":
        return "Yape (BCP)";
      case "pe.com.interbank.mobilebanking":
        return "Plin (Interbank)";
      case "com.bbva.nxt_peru":
        return "BBVA";
      case "pe.com.scotiabank.blpm.android":
        return "Scotiabank";
      case "com.scotiabank.pe.superdigital.atn":
        return "Tunki (Scotiabank)";
      default:
        return dominio.split('.').pop() || dominio;
    }
  };

  const getBancoColor = (dominio) => {
    switch (dominio) {
      case "com.bcp.innovacxion.yapeapp":
        return "#6a0dad";
      case "pe.com.interbank.mobilebanking":
        return "#00c853";
      case "com.bbva.nxt_peru":
        return "#004481";
      case "pe.com.scotiabank.blpm.android":
        return "#ed1c27";
      case "com.scotiabank.pe.superdigital.atn":
        return "#ff6b00";
      default:
        return "#888";
    }
  };

  useEffect(() => {
    const fetchBancos = async () => {
      try {
        const user = getCurrentUser();
        if (!user || !user.id) return;

        const response = await axiosInstance.get("/dashboard");

        if (response.data.success) {
          setBancos(response.data.bancos || []);
          // Calculate total deposits
          const total = (response.data.bancos || []).reduce((sum, b) => sum + Number(b.cantidad || 0), 0);
          setTotalDepositos(total);
        }
      } catch (error) {
        // Silent error
      } finally {
        setLoading(false);
      }
    };

    fetchBancos();
  }, []);

  const pieData = {
    labels: bancos.map((b) => formatBancoName(b.dominio)),
    data: bancos.map((b) => Number(b.cantidad || 0)),
  };

  return (
    <Card>
      <VuiBox
        p={3}
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: "center",
          justifyContent: "space-between",
          gap: 3,
          minHeight: "400px",
        }}
      >
      {loading ? (
        <LinearProgress sx={{ width: "100%" }} />
      ) : (
        <>
          {/* Gráfico de pastel */}
          <VuiBox
            sx={{
              width: { xs: "100%", md: "45%" },
              height: "350px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <PieChart labels={pieData.labels} data={pieData.data} />
          </VuiBox>

          {/* Información de bancos */}
          <VuiBox
            sx={{
              width: { xs: "100%", md: "55%" },
              height: "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              gap: 2,
            }}
          >
            <VuiTypography variant="h4" color="white" fontWeight="bold" mb={2}>
              💳 Bancos usados
            </VuiTypography>

            {bancos.length > 0 ? (
              <>
                <VuiBox display="flex" flexDirection="column" gap={2}>
                  {bancos.map((b, i) => {
                    const percentage = totalDepositos > 0 
                      ? ((Number(b.cantidad) / totalDepositos) * 100).toFixed(1) 
                      : 0;
                    
                    return (
                      <VuiBox
                        key={i}
                        sx={{
                          backgroundColor: "rgba(255, 255, 255, 0.05)",
                          borderRadius: "12px",
                          p: 2.5,
                          border: "1px solid rgba(255, 255, 255, 0.1)",
                          transition: "all 0.3s ease",
                          "&:hover": {
                            backgroundColor: "rgba(255, 255, 255, 0.08)",
                            borderColor: getBancoColor(b.dominio),
                            transform: "translateX(5px)",
                          },
                        }}
                      >
                        {/* Nombre del banco */}
                        <VuiBox display="flex" alignItems="center" gap={1.5} mb={1.5}>
                          <VuiBox sx={{ width: "16px", height: "16px", borderRadius: "50%" }}>
                            {b.dominio === "com.bcp.innovacxion.yapeapp" ? (
                              <img src={YapeLogo} alt="Yape" style={{ width: "100%", height: "100%", borderRadius: "50%" }} />
                            ) : b.dominio === "pe.com.interbank.mobilebanking" ? (
                              <img src={PlinLogo} alt="Plin" style={{ width: "100%", height: "100%", borderRadius: "50%" }} />
                            ) : (
                              <VuiBox
                                sx={{
                                  width: "16px",
                                  height: "16px",
                                  borderRadius: "50%",
                                  backgroundColor: getBancoColor(b.dominio),
                                  boxShadow: `0 0 12px ${getBancoColor(b.dominio)}80`,
                                }}
                              />
                            )}
                          </VuiBox>
                          <VuiTypography color="white" fontSize="1.1rem" fontWeight="medium">
                            {formatBancoName(b.dominio)}
                          </VuiTypography>
                        </VuiBox>

                        {/* Depósitos y Monto en fila */}
                        <VuiBox display="flex" gap={2} alignItems="stretch">
                          {/* Cantidad de depósitos */}
                          <VuiBox
                            sx={{
                              backgroundColor: "rgba(255, 255, 255, 0.08)",
                              borderRadius: "10px",
                              px: 3,
                              py: 1.5,
                              textAlign: "center",
                              flex: 1,
                            }}
                          >
                            <VuiTypography fontSize="1.6rem" fontWeight="bold" color="white">
                              {b.cantidad}
                            </VuiTypography>
                            <VuiTypography fontSize="0.8rem" color="text" mt={0.5}>
                              depósitos
                            </VuiTypography>
                          </VuiBox>

                          {/* Monto total */}
                          <VuiBox
                            sx={{
                              backgroundColor: "rgba(0, 200, 83, 0.15)",
                              borderRadius: "10px",
                              px: 3,
                              py: 1.5,
                              textAlign: "center",
                              flex: 1,
                              border: "1px solid rgba(0, 200, 83, 0.3)",
                            }}
                          >
                            <VuiTypography fontSize="1.6rem" fontWeight="bold" color="success">
                              S/. {formatCurrency(b.total)}
                            </VuiTypography>
                            <VuiTypography fontSize="0.8rem" color="success" sx={{ opacity: 0.8 }} mt={0.5}>
                              {percentage}% del total
                            </VuiTypography>
                          </VuiBox>
                        </VuiBox>
                      </VuiBox>
                    );
                  })}
                </VuiBox>

                {/* Resumen total */}
                <VuiBox
                  mt={2}
                  sx={{
                    backgroundColor: "rgba(106, 13, 173, 0.2)",
                    borderRadius: "12px",
                    p: 2.5,
                    border: "1px solid rgba(106, 13, 173, 0.4)",
                  }}
                >
                  <VuiBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <VuiTypography color="white" fontWeight="bold" fontSize="1.2rem">
                      Total general
                    </VuiTypography>
                  </VuiBox>
                  <VuiBox display="flex" gap={2}>
                    <VuiBox
                      sx={{
                        backgroundColor: "rgba(255, 255, 255, 0.1)",
                        borderRadius: "10px",
                        px: 3,
                        py: 1.5,
                        textAlign: "center",
                        flex: 1,
                      }}
                    >
                      <VuiTypography fontSize="1.6rem" fontWeight="bold" color="white">
                        {totalDepositos}
                      </VuiTypography>
                      <VuiTypography fontSize="0.8rem" color="text" mt={0.5}>
                        depósitos
                      </VuiTypography>
                    </VuiBox>
                    <VuiBox
                      sx={{
                        backgroundColor: "rgba(0, 200, 83, 0.2)",
                        borderRadius: "10px",
                        px: 3,
                        py: 1.5,
                        textAlign: "center",
                        flex: 1,
                        border: "1px solid rgba(0, 200, 83, 0.4)",
                      }}
                    >
                      <VuiTypography fontSize="1.6rem" fontWeight="bold" color="success">
                        S/. {formatCurrency(bancos.reduce((sum, b) => sum + parseFloat(b.total || 0), 0))}
                      </VuiTypography>
                      <VuiTypography fontSize="0.8rem" color="text" mt={0.5}>
                        total
                      </VuiTypography>
                    </VuiBox>
                  </VuiBox>
                </VuiBox>
              </>
            ) : (
              <VuiTypography color="text" mt={1}>
                No hay depósitos registrados.
              </VuiTypography>
            )}
          </VuiBox>
        </>
      )}
      </VuiBox>
    </Card>
  );
}

export default BancosUsados;
